import { useTheme } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { LDSingleKindContext, useLDClient, useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Userpilot } from 'userpilot';
import { allInvestmentRoundsMapStateJ } from '../pages/Finance2/state/FinanceStateJ';
import { useToken } from '../services/hooks/useToken';
import { MaggieHttp } from '../services/MaggieHttp';
import { fetchAppConfig } from '../services/queries/MaggieConfigQueries';
import { getAllPreferences } from '../services/queries/MaggiePreferencesQueries';
import {
  fundsByIdMapAtom,
  initConfigsWithMeta,
  roundsByIdMapAtom,
  sectorsByIdMapAtom,
  transactionTypesByIdMapAtom,
  usersByIdMapAtom,
} from '../services/state/AppConfigStateJ';
import { authenticatedUserState, sessionExpiredState } from '../services/state/AuthState';
import { searchApiConfigAtom } from '../services/state/SearchConfigStateJ';
import { basic_formatters } from '../test-utils/test-scenarios/app-config.scenario';
import { LoadingStatus } from '../types';
import { AgFormatterService } from '../util/ag-formatter-service';
import { ColourSchemeGenerator } from '../util/ColourSchemeGenerator';
import { setRumOrgContext, setRumUser } from '../util/datadogContext';
import { getEnvironment, isDemo, isDevelopment, isLocal } from '../util/environment';
import { MaggieFeatureFlags } from '../util/feature-flags';
import { FormatterService } from '../util/formatter-service';
import { FundFormatter } from '../util/formatters/FundFormatter';
import { InvestmentRoundFormatter } from '../util/formatters/InvestmentRoundFormatter';
import { RoundFormatter } from '../util/formatters/RoundFormatter';
import { SectorFormatter } from '../util/formatters/SectorFormatter';
import { StageFormatter } from '../util/formatters/StageFormatter';
import { TransactionTypeFormatter } from '../util/formatters/TransactionTypeFormatter';
import { UserFormatter } from '../util/formatters/UserFormatter';
import { getForesightStore } from '../util/jotai-store';
import { setLdClient } from '../util/ld-client';
import { ParserService } from '../util/parser-service';
import { FundParser } from '../util/parsers/FundParser';
import { PreferencesServiceV3 } from '../util/preferences-service-v3';

import { transactionTypeServiceState } from '../util/transaction-type-service';
import { logout } from '../services/hooks/useAuthActions';
import { useCleanLocalStorageShortcut } from './useCleanLocalStorageShortcut';
import { useCopyTokenToConsole } from './useCopyTokenToConsole';
import { useInitSocket } from './useInitSocket';

const emailDomainRegex = /(.*)@(.*)(.com)/;

export function useInitApp() {
  const colourGenStatus = useInitColourGenerator();
  const connectionStatus = useInitConnections();
  const appConfigStatus = useInitAppConfig(connectionStatus);
  const launchDarklyStatus = useInitLaunchDarkly();
  const preferencesStatus = useInitPreferencesServiceV3();

  useInitSentry();
  useInitDatadogRum();
  useInitPendo();
  useInitUserPilot();

  useCopyTokenToConsole();
  useCleanLocalStorageShortcut();
  useInitSocket();

  return useMemo(() => {
    const appOverallStatus = getAggregateStatus([
      appConfigStatus,
      colourGenStatus,
      connectionStatus,
      launchDarklyStatus,
      preferencesStatus,
    ]);

    return {
      appConfig: appConfigStatus,
      colorGenerator: colourGenStatus,
      connectionStatus: connectionStatus,
      overallStatus: appOverallStatus,
    };
  }, [appConfigStatus, colourGenStatus, connectionStatus, launchDarklyStatus, preferencesStatus]);
}

function useInitColourGenerator() {
  const theme = useTheme();
  ColourSchemeGenerator.init(theme.colors);

  return LoadingStatus.success;
}

function useInitSentry() {
  const user = useAtomValue(authenticatedUserState);

  useEffect(() => {
    if (isLocal() || isDevelopment() || !import.meta.env.VITE_SENTRY_DSN || !user?.email) {
      return;
    }

    Sentry.setUser({
      email: user?.email,
    });
  }, [user]);
}

export function useInitDatadogRum() {
  const user = useAtomValue(authenticatedUserState);

  useEffect(() => {
    if (!user) {
      return;
    }

    setRumUser({ id: user.id, email: user.email });
    setRumOrgContext(user.tenantId);
  }, [user]);
}

function useInitConnections() {
  const user = useAtomValue(authenticatedUserState);
  const getToken = useToken();
  const setSessionExpired = useSetAtom(sessionExpiredState);
  const [status, setStatus] = useState(LoadingStatus.loading);

  useEffect(() => {
    if (user === undefined || status !== LoadingStatus.loading) {
      return;
    }

    const sharedConfig = {
      getToken,
      on401Callback: () => setSessionExpired(true),
    };

    MaggieHttp.init(sharedConfig);

    setStatus(LoadingStatus.success);
  }, [getToken, setSessionExpired, status, user]);

  return status;
}

function useInitAppConfig(connectionStatus: LoadingStatus) {
  const [status, setStatus] = useState(LoadingStatus.idle);
  const initFormatters = useInitFormatters();
  const initServices = useInitServices();
  const initParsers = useInitParsers();

  useEffect(() => {
    if (status === LoadingStatus.idle && connectionStatus === LoadingStatus.success) {
      setStatus(LoadingStatus.loading);

      fetchAppConfig()
        .then((configMeta) => {
          try {
            initConfigsWithMeta(getForesightStore(), cloneDeep(configMeta));

            FormatterService.initService([...basic_formatters(), ...configMeta.formatters]);
            AgFormatterService.initService();
            ParserService.initService();

            return Promise.all([initFormatters(), initServices(), initParsers()]);
          } catch (err) {
            console.error(err);
            FormatterService.initService(basic_formatters());
          }
        })
        .then(() => {
          setStatus(LoadingStatus.success);
        })
        .catch((error) => {
          const httpStatus = error?.response?.status;
          if (httpStatus === 401 || httpStatus === 403) {
            const guard = `fs_${httpStatus}_logout`;
            if (!sessionStorage.getItem(guard)) {
              sessionStorage.setItem(guard, '1');
              logout();
              return;
            }
          }
          setStatus(LoadingStatus.error);
        });
    }
  }, [connectionStatus, initFormatters, initParsers, initServices, status]);

  return status;
}

function useInitPendo() {
  const user = useAtomValue(authenticatedUserState);

  useEffect(() => {
    const email = user?.email ?? '';

    if (isLocal() || !email) {
      return;
    }

    const domain = email.match(emailDomainRegex)?.[2] ?? email;
    window.pendo.initialize({
      visitor: {
        id: email,
        environment: getEnvironment(),
      },
      account: {
        id: domain,
      },
    });
  }, [user?.email]);
}

function useInitUserPilot() {
  const user = useAtomValue(authenticatedUserState);

  useEffect(() => {
    const userPilotEnabled = isDemo() && user && import.meta.env.VITE_USER_PILOT_TOKEN;

    if (!userPilotEnabled) {
      return;
    }

    Userpilot.initialize(import.meta.env.VITE_USER_PILOT_TOKEN);
    Userpilot.identify(user.email, {
      name: user.name,
      email: user.email,
      created_at: user.createdAt,
    });
  }, [user]);
}

function useInitLaunchDarkly() {
  const [status, setStatus] = useState(LoadingStatus.loading);
  const user = useAtomValue(authenticatedUserState);
  const ldClient = useLDClient();
  const { showSourcing } = useFlags<MaggieFeatureFlags>();
  const setSearchApiConfig = useSetAtom(searchApiConfigAtom);

  useEffect(() => {
    if (!user) {
      return;
    }

    const email = user.email;
    const ldUser: LDSingleKindContext = {
      kind: 'user',
      key: email,
      email,
    };

    ldClient?.identify(ldUser).then(() => {
      // Store client for use outside React (see getLdFlag in src/util/ld-client.ts)
      setLdClient(ldClient);
      setStatus(LoadingStatus.success);
    });
  }, [ldClient, user]);

  useEffect(() => {
    setSearchApiConfig({
      portfolio: true,
      sourcing: showSourcing === true,
    });
  }, [showSourcing, setSearchApiConfig]);

  return status;
}

function getAggregateStatus(statuses: LoadingStatus[]) {
  for (const status of statuses) {
    if (status === LoadingStatus.error) {
      return LoadingStatus.error;
    }
    if (status === LoadingStatus.loading) {
      return LoadingStatus.loading;
    }
  }

  return LoadingStatus.success;
}

export function useInitParsers() {
  return useAtomCallback(
    useCallback(async (get) => {
      const fundsById = get(fundsByIdMapAtom);
      const parserService = ParserService.get();
      parserService.setParserForId('fund', new FundParser().init(fundsById).parse);
    }, [])
  );
}

export function useInitFormatters() {
  return useCallback(async () => {
    const store = getForesightStore();
    const fundsById = store.get(fundsByIdMapAtom);
    const roundsById = store.get(roundsByIdMapAtom);
    const sectorsById = store.get(sectorsByIdMapAtom);
    const transactionTypesById = store.get(transactionTypesByIdMapAtom);
    const usersById = store.get(usersByIdMapAtom);
    const transTypeFormatter = new TransactionTypeFormatter().init(transactionTypesById);
    const service = FormatterService.get();

    service.setFormatterForId('fund', new FundFormatter().init(fundsById).format);
    service.setFormatterForId('round', new RoundFormatter().init(roundsById).format);
    service.setFormatterForId('sector', new SectorFormatter().init(sectorsById).format);
    service.setFormatterForId('transactionType', transTypeFormatter.format);
    service.setFormatterForId('transactionCategory', transTypeFormatter.formatCategory);
    service.setFormatterForId('transactionSubType', transTypeFormatter.formatSubType);
    service.setFormatterForId('user', new UserFormatter().init(usersById).format);
    service.setFormatterForId('userByEmail', new UserFormatter().init(usersById).formatByEmail);

    try {
      const investmentRounds = await store.get(allInvestmentRoundsMapStateJ);
      service.setFormatterForId(
        'investmentRound',
        new InvestmentRoundFormatter().init(investmentRounds).format
      );
      service.setFormatterForId(
        'stage',
        new StageFormatter().init({ investmentMap: investmentRounds, stageMap: roundsById }).format
      );
    } catch (error) {
      console.error(error);
    }
  }, []);
}

export function useInitPreferencesServiceV3() {
  const [status, setStatus] = useState(LoadingStatus.loading);
  const initPrefs = useCallback(async () => {
    try {
      const preferences = await getAllPreferences();
      PreferencesServiceV3.initService(preferences);
      setStatus(LoadingStatus.success);
    } catch (error) {
      PreferencesServiceV3.initService([]);
      console.error(error);
      setStatus(LoadingStatus.success);
    }
  }, []);

  useEffect(() => {
    initPrefs();
  }, [initPrefs]);

  return status;
}

export function useInitServices() {
  return useAtomCallback(
    useCallback((get) => {
      // Reading the atom triggers TransactionTypeService.initService as a
      // side effect (see transaction-type-service.ts).
      get(transactionTypeServiceState);
    }, [])
  );
}
